<template>
  <div class="register-pform-view">
    <div
      v-if="isInitializing"
      class="loading"
    >
      <unc-loader />
    </div>

    <div
      v-else
      class="row"
    >
      <div class="col-md-12 col-sm-12 col-xs-12">
        <div class="register-card">
          <div class="register-glow" />

          <div class="card-header ch-alt register-header">
            <div class="register-kicker rr-kicker--info">
              Paso 3 de 4
            </div>
            <h1 class="register-title">
              {{ rolesCoinciden ? 'Verificá tus datos' : 'Completá tus datos' }}
            </h1>
            <p class="register-subtitle">
              Si tenés alguna duda, enviá un correo al <a
                href="/contactos"
                target="_blank"
                class="register-text-link"
                rel="noopener"
              >administrador de tu dependencia o facultad<span class="visually-hidden"> (se abre en una pestaña nueva)</span></a>.
            </p>
          </div>

          <div class="card-body register-body">
            <form @submit.prevent="validateBeforeSubmit">
              <!-- AUTOREGISTRO (persona sin registros previos) -->
              <template v-if="isManualRegister">
                <div class="rp-info-grid rp-info-grid--three">
                  <div class="rp-info-row">
                    <span class="rp-info-label">País</span>
                    <span class="rp-info-value">{{ searchIdentifier.countryName }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Documento</span>
                    <span class="rp-info-value">{{ item.identityType.split('_')[1] }} {{ item.identityNumber }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Rol</span>
                    <span class="rp-info-value">{{ selectedPersonRol.name }}</span>
                  </div>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('names') }"
                >
                  <div>
                    <label for="names">Nombres</label>
                    <input
                      id="names"
                      v-model="item.names"
                      v-validate="'required|nombre_apellido|max:50'"
                      name="names"
                      type="text"
                      autocomplete="given-name"
                      class="form-control"
                      placeholder="Ingresá tus nombres..."
                      :aria-describedby="errors.has('names') ? 'names-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('names')"
                    id="names-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('names') }}</small>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('lastnames') }"
                >
                  <div>
                    <label for="lastnames">Apellidos</label>
                    <input
                      id="lastnames"
                      v-model="item.lastNames"
                      v-validate="'required|nombre_apellido|max:50'"
                      name="lastnames"
                      type="text"
                      autocomplete="family-name"
                      class="form-control"
                      placeholder="Ingresá tus apellidos..."
                      :aria-describedby="errors.has('lastnames') ? 'lastnames-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('lastnames')"
                    id="lastnames-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('lastnames') }}</small>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('email') }"
                >
                  <div>
                    <label for="email">Correo de recuperación</label>
                    <input
                      id="email"
                      v-model="item.email"
                      v-validate="'required|email|isNotYuliEmail|alphanum_underscore|max:255'"
                      name="email"
                      type="text"
                      autocomplete="email"
                      inputmode="email"
                      class="form-control"
                      placeholder="Ingresá tu correo de recuperación..."
                      :aria-describedby="errors.has('email') ? 'email-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('email')"
                    id="email-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('email') }}</small>
                  <p class="rp-field-hint">
                    Enviaremos un correo a esta dirección para completar tu registro.
                  </p>
                </div>
              </template>

              <!-- GUARANÍ CON ROLES COINCIDENTES -->
              <template v-else-if="rolesCoinciden">
                <div class="rp-info-grid rp-info-grid--three">
                  <div class="rp-info-row">
                    <span class="rp-info-label">Nombres</span>
                    <span class="rp-info-value">{{ titleCase(item.names) }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Apellidos</span>
                    <span class="rp-info-value">{{ titleCase(item.lastNames) }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">País</span>
                    <span class="rp-info-value">{{ searchIdentifier.countryName }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Documento</span>
                    <span class="rp-info-value">{{ item.identityType.split('_')[1] }} {{ item.identityNumber }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Rol</span>
                    <span class="rp-info-value">{{ selectedPersonRol.name }}</span>
                  </div>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('email') }"
                >
                  <div>
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
                    <label>Correo de recuperación</label>
                    <div class="rp-readonly-value">
                      {{ item.email }}
                    </div>
                  </div>
                  <small
                    v-if="errors.has('email')"
                    id="email-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('email') }}</small>
                  <div
                    v-if="isDisabled"
                    class="rp-warning-block"
                  >
                    <svg
                      aria-hidden="true"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <span>Este correo proviene de Guaraní. El mismo no puede tener dominio @unc.edu.ar
                      o @mi.unc.edu.ar. Por favor, cambiá tu correo de recuperación desde Autogestión Guaraní. Luego, comenzá de nuevo tu registro de Usuario UNC.</span>
                  </div>
                  <p
                    v-else
                    class="rp-field-hint"
                  >
                    Este correo proviene de Guaraní y lo usaremos para continuar con tu registro.
                    <a
                      href="#"
                      role="button"
                      class="register-text-link"
                      @click.prevent="openModifyRecoveryEmailModal()"
                      @keydown.space.prevent="openModifyRecoveryEmailModal()"
                    >
                      ¿No tenés acceso a este correo?
                    </a>
                  </p>
                </div>
              </template>

              <!-- GUARANÍ SIN ROLES COINCIDENTES / YULI -->
              <template v-else>
                <div class="rp-info-grid rp-info-grid--three">
                  <div class="rp-info-row">
                    <span class="rp-info-label">País</span>
                    <span class="rp-info-value">{{ searchIdentifier.countryName }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Documento</span>
                    <span class="rp-info-value">{{ item.identityType.split('_')[1] }} {{ item.identityNumber }}</span>
                  </div>
                  <div class="rp-info-row">
                    <span class="rp-info-label">Rol</span>
                    <span class="rp-info-value">{{ selectedPersonRol.name }}</span>
                  </div>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('names') }"
                >
                  <div>
                    <label for="names">Nombres</label>
                    <input
                      id="names"
                      v-model="item.names"
                      v-validate="'required|nombre_apellido|max:50'"
                      name="names"
                      type="text"
                      autocomplete="given-name"
                      class="form-control"
                      placeholder="Ingresá tu nombre..."
                      :aria-describedby="errors.has('names') ? 'names-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('names')"
                    id="names-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('names') }}</small>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('lastnames') }"
                >
                  <div>
                    <label for="lastnames">Apellidos</label>
                    <input
                      id="lastnames"
                      v-model="item.lastNames"
                      v-validate="'required|nombre_apellido|max:50'"
                      name="lastnames"
                      type="text"
                      autocomplete="family-name"
                      class="form-control"
                      placeholder="Ingresá tu apellido..."
                      :aria-describedby="errors.has('lastnames') ? 'lastnames-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('lastnames')"
                    id="lastnames-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('lastnames') }}</small>
                </div>

                <div
                  class="form-group register-field"
                  :class="{ 'has-error': errors.has('email') }"
                >
                  <div>
                    <label for="email">Correo de recuperación</label>
                    <input
                      id="email"
                      v-model="item.email"
                      v-validate="'required|email|isNotYuliEmail|alphanum_underscore|max:255'"
                      name="email"
                      type="text"
                      autocomplete="email"
                      inputmode="email"
                      class="form-control"
                      placeholder="Ingresá tu correo de recuperación..."
                      :aria-describedby="errors.has('email') ? 'email-error' : null"
                    >
                  </div>
                  <small
                    v-if="errors.has('email')"
                    id="email-error"
                    class="help-block"
                    role="alert"
                  >{{ errors.first('email') }}</small>
                  <p class="rp-field-hint">
                    Enviaremos un correo a esta dirección para completar tu registro.
                  </p>
                </div>
              </template>

              <div class="rr-actions rp-actions">
                <button
                  type="button"
                  class="btn btn-link waves-effect register-btn register-btn-secondary"
                  @click.prevent="goBack()"
                >
                  Volver
                </button>
                <button
                  v-if="isDisabled"
                  type="button"
                  class="btn btn-primary waves-effect register-btn register-btn-primary"
                  @click.prevent="showAlert()"
                >
                  <span>Siguiente</span>
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
                <button
                  v-else
                  type="submit"
                  class="btn btn-primary waves-effect register-btn register-btn-primary"
                >
                  <span>Siguiente</span>
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <EmailHelpModal
      ref="emailHelpComponent"
      @closed="$router.push('/')"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { HttpFRONT, swal } from '../../share.js';
import { titleCase } from '../../utils/titleCase';
import withoutSpaces from '../../../utils/withoutSpaces';
import EmailHelpModal from '../utils/EmailHelpModal.vue';
import validateForm from '../../mixins/validate-form';

export default {
    name: 'RegisterPersonForm',
    components: {
        EmailHelpModal,
    },
    mixins: [validateForm],
    data () {
        return {
            isInitializing: true,
            item: {
                names: '',
                lastNames: '',
                identityType: '',
                identityNumber: '',
                email: '',
                profiles: '',
                origin: '',
            },
            guaraniUrl: null,
        };
    },
    computed: {
        isManualRegister: function () {
            return !this.currentUser;
        },
        ...mapState([
            'selectedPersonRol',
            'currentUser',
            'searchIdentifier',
            'currentUserIdentity',
        ]),
        ...mapGetters([
            'invalidDomains',
            'nextStep',
            'sharedComponentsInfo',
        ]),
        rolesCoinciden: function () {
            const profiles = this.currentUser?.profiles ?? [];
            return profiles.includes(this.selectedPersonRol?.code);
        },
        isDisabled () {
            let domains = this.invalidDomains.split(',');
            domains = domains.filter(e => !e.includes('.')).map(e => `${e}.`);
            if (!this.isManualRegister && this.rolesCoinciden) {
                return new RegExp(`.*@((${domains.join('|')}))?unc.edu.ar$`).test(this.item.email);
            }
            return false;
        },
    },
    mounted () {
        if (!this.isManualRegister && !this.rolesCoinciden) {
            this.item.email = '';
        }

        if (this.sharedComponentsInfo.names) {
            this.item.names = this.sharedComponentsInfo.names;
        }

        if (this.sharedComponentsInfo.lastNames) {
            this.item.lastNames = this.sharedComponentsInfo.lastNames;
        }

        if (this.sharedComponentsInfo.email) {
            this.item.email = this.sharedComponentsInfo.email;
        }
    },
    async created () {
        this.getData();
        if (!this.isManualRegister && this.rolesCoinciden) {
            await this.getGuaraniUrl();
        }
    },
    methods: {
        async openModifyRecoveryEmailModal () {
            try {
                await swal({
                    type: 'info',
                    title: 'Modificar correo de recuperación',
                    text: 'Este correo proviene de Guaraní, si necesitás modificarlo, deberás realizarlo desde Autogestión Guaraní. Luego, comenzá de nuevo tu registro de Usuario UNC.',
                    confirmButtonText: 'Ir a Autogestión Guaraní',
                    cancelButtonText: 'Cerrar',
                    showCancelButton: true,
                });
                window.open(this.guaraniUrl, '_blank');
            } catch {
            }
        },
        titleCase (str) {
            return titleCase(str);
        },
        getData () {
            if (this.isManualRegister) {
                // AUTOREGISTRO (No trae un usuario)
                this.item.names = '';
                this.item.lastNames = '';
                this.item.identityType = this.searchIdentifier.code;
                this.item.identityNumber = this.searchIdentifier.number;
                this.item.email = '';
                this.item.origin = 'YULI';
            } else if (this.currentUser) {
                // (Trae un usuario)
                this.item.names = this.titleCase(this.currentUser.names).trim();
                this.item.lastNames = this.titleCase(this.currentUser.lastNames).trim();
                this.item.identityType = this.currentUserIdentity?.identityType ?? this.searchIdentifier.code;
                this.item.identityNumber = this.currentUserIdentity?.identityNumber ?? this.searchIdentifier.number;
                this.item.email = this.rolesCoinciden && this.currentUser.email ? this.currentUser.email.toLowerCase() : '';
                this.item.roleCode = this.selectedPersonRol.code;
                this.item.origin = this.rolesCoinciden ? 'GUARANI' : 'YULI';
            }
            this.item.idRol = this.selectedPersonRol._id;
            this.isInitializing = false;
        },
        async getGuaraniUrl () {
            try {
                const response = await HttpFRONT.get('autogestion-guarani-url');
                this.guaraniUrl = response.data.url;
            } catch (error) {
                this.guaraniUrl = null;
            }
        },
        showAlert () {
            swal({
                title: 'Oops!',
                text: 'Tu correo de recuperación no puede ser un correo @unc.edu.ar o @mi.unc.edu.ar. Por favor, cambiá tu correo de recuperación desde Guaraní.',
                type: 'warning',
            });
        },
        async validateBeforeSubmit () {
            try {
                if (await this.validateForm()) {
                    await this.save();
                }
            } catch (error) {
                console.error('Error al validar el formulario:', error);
                swal({ title: 'Algo salió mal', text: 'Tus datos no fueron registrados. Por favor, intentá nuevamente más tarde.', type: 'warning' });
            }
        },
        async save () {
            this.stashInfo();
            this.isInitializing = true;
            const personToSend = {
                names: this.titleCase((this.item.names).trim()),
                lastNames: this.titleCase((this.item.lastNames).trim()),
                identityNumber: withoutSpaces(this.item.identityNumber),
                identityType: this.item.identityType,
                profile: this.item.idRol,
                profileCode: this.item.roleCode,
                origin: this.item.origin,
                mainEmail: this.item.email.toLowerCase(),
            };

            if (this.currentUser?.sessionKey) {
                personToSend.sessionKey = this.currentUser.sessionKey;
            }

            if (this.nextStep === 'START_REGISTRATION') {
                try {
                    const response = await HttpFRONT.post('personas', personToSend);

                    if (response.status === 201 || response.status === 200) {
                        try {
                            this.$router.push('/');
                            await swal({
                                title: '¡Correo enviado!',
                                html: `Enviamos un correo a <b>${this.item.email}</b>.<br>Ingresá y seguí las instrucciones para continuar.`,
                                type: 'info',
                                showCancelButton: true,
                                confirmButtonText: 'Entendido (5s)',
                                cancelButtonText: 'No recibí el correo',
                                cancelButtonColor: '#aaa',
                                allowOutsideClick: false,
                                allowEscapeKey: false,
                                onOpen: () => {
                                    swal.disableConfirmButton();
                                    const confirmButton = swal.getConfirmButton();

                                    if (confirmButton) {
                                        confirmButton.style.opacity = '0.55';
                                        confirmButton.style.cursor = 'not-allowed';
                                    }

                                    let timeLeft = 5;

                                    const interval = setInterval(() => {
                                        timeLeft -= 1;
                                        if (confirmButton) {
                                            confirmButton.textContent = `Entendido (${timeLeft}s)`;
                                        }

                                        if (timeLeft <= 0) {
                                            clearInterval(interval);
                                            if (confirmButton) {
                                                confirmButton.textContent = 'Entendido';
                                                confirmButton.style.opacity = '1';
                                                confirmButton.style.cursor = 'pointer';
                                            }
                                            swal.enableConfirmButton();
                                        }
                                    }, 1000);
                                },
                            });
                        } catch (dismiss) {
                            if (dismiss === 'cancel') {
                                if (this.$refs.emailHelpComponent) {
                                    this.$refs.emailHelpComponent.open();
                                }
                            }
                        }
                    }
                } catch (error) {
                    await this.handleSaveError(error);
                } finally {
                    this.isInitializing = false;
                }
            }
        },
        extractErrorMessage (payload) {
            if (!payload) return '';
            if (typeof payload === 'string') return payload;
            if (typeof payload === 'object') {
                return payload.message || payload.error || '';
            }
            return '';
        },
        async handleSaveError (error) {
            const status = error?.response?.status;
            const message = this.extractErrorMessage(error?.response?.data);
            const helpDeskMessage = 'Por favor, comunicate con el <a href="/contactos" target="_blank" rel="noopener" class="register-text-link">administrador de tu dependencia o facultad<span class="visually-hidden"> (se abre en una pestaña nueva)</span></a>.';

            switch (status) {
            case 401:
                swal({ title: 'Acceso denegado', text: 'No posee permisos para acceder a este servicio.', type: 'warning' });
                break;
            case 400:
                swal({ title: 'Datos no guardados', text: 'Tus datos no se pudieron guardar. Por favor, intentá nuevamente más tarde.', type: 'warning' });
                break;
            case 409:
                if (message === 'Correo de recuperación ya asociado a un usuario existente.') {
                    swal({ title: 'Algo salió mal', text: message, type: 'warning' });
                } else if (message === 'Another person already has this identity.') {
                    swal({ title: 'Algo salió mal', html: 'Otra persona ya posee este documento. ' + helpDeskMessage, type: 'warning' });
                } else if (message === 'Another person already has the DNI corresponding to the provided CUIL.') {
                    swal({ title: 'Algo salió mal', html: 'Otra persona ya posee el DNI correspondiente al CUIL ' + this.item.identityNumber + '. ' + helpDeskMessage, type: 'warning' });
                } else if (message === 'Another person already has a CUIL corresponding to the provided DNI.') {
                    swal({ title: 'Algo salió mal', html: 'Otra persona ya posee un CUIL correspondiente al DNI ' + this.item.identityNumber + '. ' + helpDeskMessage, type: 'warning' });
                } else if (message === 'Another person already has the same Guaraní ID.') {
                    swal({ title: 'Algo salió mal', html: 'Otra persona ya está registrada en Guaraní con este documento. ' + helpDeskMessage, type: 'warning' });
                } else {
                    swal({ title: 'Algo salió mal', text: helpDeskMessage, type: 'warning' });
                }
                break;
            case 422:
                if (message === 'La sesión ha expirado. Por favor, vuelva a iniciar el proceso de registro.') {
                    await swal({ title: 'Datos no guardados', text: message, type: 'warning', allowOutsideClick: false, allowEscapeKey: false });
                    window.location.reload();
                } else if (message === 'The provided DNI and CUIL do not correspond to each other.') {
                    await swal({ title: 'Datos inválidos', text: 'El DNI y el CUIL ingresados no se corresponden entre sí.', type: 'warning' });
                } else if (message.includes('is not a valid')) {
                    await swal({ title: 'Datos inválidos', text: 'El número ' + this.item.identityNumber + ' no es un ' + this.item.identityType + ' válido.', type: 'warning' });
                } else if (message === 'Only one DNI and one CUIL are allowed') {
                    await swal({ title: 'Datos inválidos', text: 'Solo se permite ingresar un DNI y un CUIL.', type: 'warning' });
                } else {
                    await swal({ title: 'Datos no guardados', text: 'Tus datos no se pudieron guardar. Por favor, intentá nuevamente más tarde.', type: 'warning' });
                }
                break;
            case 500:
                swal({ title: 'Algo salió mal', text: 'Servicio temporalmente no disponible. Por favor, intentá nuevamente más tarde.', type: 'warning' });
                break;
            default:
                await swal({ title: 'Datos no guardados', text: 'Tus datos no se pudieron guardar. Por favor, intentá nuevamente más tarde.', type: 'warning' });
                break;
            }
        },
        goBack () {
            this.stashInfo();
            this.$store.commit('setCurrentComponent', 'register-person-rol');
        },
        stashInfo () {
            this.$store.commit('setPersonInfo', {
                names: this.item.names,
                lastNames: this.item.lastNames,
                email: this.item.email,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.register-pform-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);
}

.rp-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 1px;
  background: rgba(0, 56, 101, 0.08);
  border: 1px solid rgba(0, 56, 101, 0.1);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 18px;
}

.rp-info-grid--three .rp-info-row:last-child {
  grid-column: 1 / -1;
}

.rp-info-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 14px;
  background: rgba(246, 250, 253, 0.95);
  transition: background 0.2s ease;
}

.rp-info-label {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--register-azul-mid);
}

.rp-info-value {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 500;
  color: var(--register-ink);
}

.rp-readonly-value {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 500;
  color: var(--register-ink);
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(0, 56, 101, 0.04);
  border: 1px solid rgba(0, 56, 101, 0.1);
}

.rp-field-hint {
  margin-top: 8px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--register-muted);
  line-height: 1.65;
}

.rp-warning-block {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 14px;
  background: rgba(200, 50, 70, 0.06);
  border: 1px solid rgba(200, 50, 70, 0.2);
  border-radius: 10px;
  color: #a01e34;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.65;
}

.rp-warning-block svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.rp-actions {
  margin-top: 12px;
}

.register-pform-view .register-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (min-width: 720px) and (max-width: 768px) {
  .rp-info-grid--three {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rp-info-grid--three .rp-info-row:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .rp-info-grid {
    grid-template-columns: 1fr;
  }

  .rp-actions {
    flex-direction: column-reverse;
    justify-content: stretch;
    gap: 8px;
  }

  .rp-actions .register-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
